/**
 * "Paste to fill" for people and company records (roadmap #19/#27, D18):
 * an email signature, a WhatsApp message or a business card read BY CODE —
 * in the browser, nothing sent anywhere, so a new person's or company's name
 * never reaches the AI. The result only proposes values; the person picks
 * which go into the form and saves it.
 */
import { chatSenders, emailSender, withoutChatStamps } from "./stripMessage";
import { isKnownPlaceName } from "./arabicText";

export interface ParsedPhone {
    number: string;
    kind: "mobile" | "office" | "other";
}

export interface ParsedSignature {
    person: string | null;
    company: string | null;
    title: string | null;
    emails: string[];
    phones: ParsedPhone[];
    website: string | null;
}

const EMAIL = /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g;
const PHONE = /(?:\+|\b00)?\d[\d\s().\-–]{6,}\d/g;
const WEBSITE = /\b(?:https?:\/\/)?(?:www\.)?[a-z0-9][a-z0-9-]*(?:\.[a-z0-9-]+)*\.[a-z]{2,}(?:\/[^\s]*)?/gi;
const COMPANY_WORDS = /\b(?:llc|l\.l\.c|ltd|limited|fze|fzco|fz-llc|dmcc|inc|corp|gmbh|plc|company|group|holdings?|services|security|logistics|transport|solutions|consulting|trading|international|est|establishment)\b|\b(?:co|s\.a|est)\.(?!\w)/i;
/** A company's legal form: the surest sign a line is its name. */
const LEGAL_WORDS = /\b(?:llc|l\.l\.c|ltd|limited|fze|fzco|fz-llc|dmcc|inc|corp|gmbh|plc|company|group|holdings?|est|establishment)\b|\b(?:co|s\.a|est)\.(?!\w)/i;
const TITLE_WORDS =/\b(manager|director|officer|head|ceo|cfo|coo|cto|founder|co-founder|partner|owner|coordinator|specialist|engineer|consultant|supervisor|lead|executive|president|vice|vp|advisor|analyst|agent|representative|sales|operations|procurement)\b/i;
const NOISE = /^(regards|best regards|kind regards|thanks|thank you|sent from|cheers|br|sincerely)\b|^(?:مع\s+(?:أطيب|اطيب|خالص)\s+التحيات|تحياتي|شكرا|شكراً|وتفضلوا بقبول)/i;
// Arabic signatures (2026-10-15): company words and legal forms, job titles, honorifics before a name
const ARABIC_LEGAL = /(?:^|\s)(?:شركة|مؤسسة|مجموعة)(?:\s|$)|ذ\.?\s?م\.?\s?م|المحدودة|القابضة/;
const ARABIC_COMPANY = new RegExp(`${ARABIC_LEGAL.source}|للخدمات|للتجارة|للنقل|للحراسات|للحراسة|الأمنية|للسياحة|للمقاولات|للاستشارات`);
const ARABIC_TITLE = /(?:^|\s)(?:مدير|المدير|مديرة|مسؤول|مسؤولة|منسق|منسقة|مهندس|مهندسة|رئيس|رئيسة|نائب|مستشار|مستشارة|مندوب|مندوبة|مشرف|مشرفة|أخصائي|اخصائي|محاسب|محاسبة|ضابط)(?:\s|$)/;
const HONORIFIC = /^(?:الأستاذ|الاستاذ|الأستاذة|الاستاذة|السيد|السيدة|المهندس|المهندسة|الدكتور|الدكتورة|د\.|م\.|أ\.)\s+/;
const isTitle = (line: string): boolean => TITLE_WORDS.test(line) || ARABIC_TITLE.test(line);

const clean = (text: string): string => text.replace(/\s+/g, " ").trim();

/** "Legal form Limited Liability Company", "Licensed activities …": a labelled field, not a company line. */
const LABELLED_LINE = /^(legal\s+form|licensed\s+activities|activities|address|phone|email|website|manager|tax\s+registration|issue\s+date|expiry)\b/i;

/** Two strings at most two edits apart (OCR slips). */
const withinTwoEdits = (a: string, b: string): boolean => {
    if (Math.abs(a.length - b.length) > 2) return false;
    const row = Array.from({ length: b.length + 1 }, (_, j) => j);
    for (let i = 1; i <= a.length; i++) {
        let previous = row[0];
        row[0] = i;
        for (let j = 1; j <= b.length; j++) {
            const kept = row[j];
            row[j] = Math.min(row[j] + 1, row[j - 1] + 1, previous + (a[i - 1] === b[j - 1] ? 0 : 1));
            previous = kept;
        }
    }
    return row[b.length] <= 2;
};

/** A phone's kind from the words just before it on its line. */
const phoneKind = (before: string): ParsedPhone["kind"] | null => {
    const label = before.toLowerCase();
    if (/\b(fax|f)\s*[:.]?\s*$/.test(label)) return null;
    if (/\b(m|mob|mobile|cell|gsm|whatsapp|wa)\s*[:.]?\s*$/.test(label) || /(?:جوال|موبايل|محمول|نقال|واتساب|واتس)\s*[:.]?\s*$/.test(label)) return "mobile";
    if (/\b(t|tel|phone|office|direct|landline|p)\s*[:.]?\s*$/.test(label) || /(?:هاتف|تلفون|تليفون|مكتب|ت)\s*[:.]?\s*$/.test(label)) return "office";
    if (/(?:فاكس)\s*[:.]?\s*$/.test(label)) return null;
    return "other";
};

/** Looks like a person's name: 2–4 words of letters, no digits or company words. */
const looksLikeName = (line: string): boolean => {
    const text = clean(line).replace(HONORIFIC, "");
    if (!text || /\d|@|\bwww\b|https?:/i.test(text) || COMPANY_WORDS.test(text) || ARABIC_COMPANY.test(text) || isTitle(text)) return false;
    // an Arabic greeting isn't a person ("السلام عليكم", "مرحبا أحمد")
    if (/^(?:السلام|مرحبا|مرحباً|أهلا|اهلا|عزيزي|عزيزتي|الأخ|الاخ)(?:\s|$)/.test(text)) return false;
    const words = text.split(" ");
    // a greeting isn't a person ("Hi Crownox", "Dear Team")
    if (/^(?:hi|hello|hey|dear|regards|thanks|attn|team|to|from)$/i.test(words[0] ?? "")) return false;
    // a brand in capitals isn't a person ("NAJD ARMOURED MOBILITY")
    if (/^[\p{Lu}\s'.-]+$/u.test(text) && /[A-Z]{3}/.test(text)) return false;
    if (words.filter((word) => /^[A-Z]{3,}$/.test(word)).length >= 2) return false;
    // each word starts with a capital (or a letter of a script without case, e.g. Arabic)
    return words.length >= 2 && words.length <= 4 && words.every((word) => /^[\p{Lu}\p{Lo}][\p{L}'.-]*$/u.test(word));
};

/** "acme-logistics.ae" → "Acme Logistics" (a fallback company name). */
const companyFromDomain = (domain: string): string | null => {
    const base = domain.toLowerCase().replace(/^www\./, "").split(".")[0];
    if (!base || ["gmail", "hotmail", "outlook", "yahoo", "icloud", "live", "msn", "aol", "proton", "protonmail"].includes(base)) return null;
    return base.split(/[-_]/).filter(Boolean).map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
};

/**
 * Read a signature, message or card text.
 * @param text - What was pasted, or what OCR read off a card.
 */
/**
 * Text read off an Arabic or bilingual card: direction marks off, and a number
 * OCR wrote backwards on a right-to-left line ("0123 555 56 971+") put back
 * the right way round ("+971 56 555 0123").
 */
const rightToLeftFixed = (text: string): string => text
    .replace(/[\u200E\u200F\u202A-\u202E\u2066-\u2069]/g, "")
    .replace(/(?<![\d+])((?:\d{1,4}\s+){2,5}\d{1,4})\+(?!\d)/g, (_match, digits: string) => `+${digits.trim().split(/\s+/).reverse().join(" ")}`);

export const parseSignature = (raw: string): ParsedSignature => {
    const pasted = rightToLeftFixed(raw);
    // a WhatsApp export: the sender is the person (or the number shown for an unsaved contact); read the messages without stamps
    const senders = chatSenders(pasted);
    const text = senders.length ? withoutChatStamps(pasted) : pasted;
    const senderPhone = senders.find((sender) => /^\+?[\d\s().-]{8,}$/.test(sender)) ?? null;
    const sender = [...senders, emailSender(pasted) ?? ""].find((name) => !!name && looksLikeName(name)) ?? null;
    const lines = text.split(/\r?\n/).map(clean).filter((line) => line && !NOISE.test(line));
    const emails = Array.from(new Set(Array.from(text.matchAll(EMAIL), (match) => match[0].toLowerCase())));

    const phones: ParsedPhone[] = senderPhone ? [{ number: senderPhone, kind: "mobile" }] : [];
    lines.forEach((line) => {
        for (const match of Array.from(line.matchAll(PHONE))) {
            const digits = match[0].replace(/\D/g, "");
            if (digits.length < 8 || digits.length > 15) continue;
            const before = line.slice(0, match.index ?? 0);
            if (/\b(trn|vat|tax|licen[cs]e|registration|reg\.?|cr|iban|account)\b[^\d]{0,24}$/i.test(before)) continue;
            const kind = phoneKind(before);
            if (!kind) continue;
            const number = match[0].replace(/[–]/g, "-").replace(/\s+/g, " ").trim();
            if (!phones.some((phone) => phone.number.replace(/\D/g, "") === digits)) phones.push({ number, kind });
        }
    });

    const emailDomains = new Set(emails.map((email) => email.split("@")[1]));
    const domain = (emails[0]?.split("@")[1] ?? null);
    const website = Array.from(text.replace(EMAIL, " ").matchAll(WEBSITE), (match) => match[0])
        .find((candidate) => /[a-z]/i.test(candidate) && (/^(https?:|www\.)/i.test(candidate) || candidate.includes("/") || emailDomains.has(candidate.toLowerCase()) || /\.(com|net|org|ae|sa|jo|ps|qa|io|co|uk|de)\b/i.test(candidate)))
        ?? null;

    // "this is Sara Khoury from Acme Logistics" (messages)
    const intro = /\b(?:this is|i am|i'm|my name is)\s+([\p{L}][\p{L}'.-]*(?:\s+(?!(?:from|at|with)\b)[\p{L}][\p{L}'.-]*){0,2})(?:\s+(?:from|at|with)\s+([^,.\n]+))?/iu.exec(text);

    const titleLine = lines.find((line) => isTitle(line) && !ARABIC_LEGAL.test(line) && !/@|\d{4}/.test(line) && line.split(" ").length <= 8) ?? null;
    // cards read by OCR: trailing marks and stray symbols around the title
    const title = titleLine ? titleLine.replace(/^[^\p{L}]+|[^\p{L}.)]+$/gu, "").trim() || null : null;
    // "We are Najd Armoured Mobility, a licensed …" (introduction emails)
    const weAre = /\bwe are\s+([\p{Lu}][^,.\n(]{2,60}?)(?=,|\.|\s+\(|\s+(?:a|an|the)\s)/u.exec(text);
    // a line that spells the email / web domain ("App Review Demo Client" ↔ appreview-democlient.ae)
    const webDomain = (website ?? "").replace(/^https?:\/\//i, "").replace(/^www\./i, "").split("/")[0] || domain || "";
    const domainKey = webDomain.split(".")[0].toLowerCase().replace(/[^a-z0-9]/g, "");
    const lineKey = (line: string) => line.toLowerCase().replace(/[^a-z0-9]/g, "");
    // (a name, not a sentence about it: "Blue Line Security is a licensed security company …")
    const domainLine = domainKey.length >= 5 ? lines.find((line) => !/@|www\.|https?:/i.test(line) && line.split(" ").length <= 8 && lineKey(line).length >= 5 && (domainKey.startsWith(lineKey(line)) || lineKey(line).startsWith(domainKey.replace(/demo$/, "")) || domainKey === lineKey(line))) : null;
    // a brand line in capitals ("NAJD ARMOURED MOBILITY")
    const capsLine = lines.find((line) => /^[\p{Lu}][\p{Lu}\s&.'-]{4,}$/u.test(line) && line.split(" ").length >= 2 && line.split(" ").length <= 6 && !isTitle(line));
    const companyLine = (words: RegExp) => lines.find((line) => words.test(line) && !/@/.test(line) && line.split(" ").length <= 8 && line !== title && !LABELLED_LINE.test(line));
    // a legal form ("Najd Armoured Mobility Co.") is surer than an activity word ("Licensed security transport services", a "Secure Transport" heading)
    const legalLine = companyLine(LEGAL_WORDS);
    const arabicLegalLine = companyLine(ARABIC_LEGAL);
    const activityLine = companyLine(COMPANY_WORDS) ?? companyLine(ARABIC_COMPANY);
    const namedCompany = legalLine
        ?? domainLine
        ?? capsLine
        ?? arabicLegalLine
        ?? (intro?.[2] ? clean(intro[2]) : null)
        ?? (weAre?.[1] ? clean(weAre[1]) : null)
        ?? null;
    const domainCompany = emails[0] ? companyFromDomain(emails[0].split("@")[1]) : website ? companyFromDomain(website.replace(/^https?:\/\//, "")) : null;
    let company = namedCompany ?? activityLine ?? domainCompany;
    // the name usually sits right above the title; OCR may glue noise after it
    const aboveTitle = titleLine ? lines[lines.indexOf(titleLine) - 1] : undefined;
    const leadingName = aboveTitle ? (/^((?:[\p{Lu}][\p{L}'.-]+\s+){1,3}[\p{Lu}][\p{L}'.-]+)/u.exec(aboveTitle)?.[1] ?? null) : null;
    // a licence or profile names its manager / contact ("Manager Omar Al Suwaidi"; OCR may drop the r)
    const labelled = lines
        .map((line) => /^(?:manager?|general manager|contact person|attention|attn\.?)\b\s*[:-]?\s+([\p{L}][\p{L}'.-]*(?:\s+[\p{L}][\p{L}'.-]*){1,3})$/iu.exec(line)?.[1])
        .map((name) => name?.replace(/(^|\s)(\p{Ll})/gu, (_, gap: string, letter: string) => gap + letter.toUpperCase()))
        .find((name): name is string => !!name && looksLikeName(name)) ?? null;
    // in a chat only the sender is the person (a message line like "Demo Hotel" isn't)
    const person = senders.length ? sender : (intro?.[1] && looksLikeName(intro[1]) ? clean(intro[1]) : null)
        ?? sender
        ?? labelled
        ?? (leadingName && looksLikeName(leadingName) && leadingName !== company ? leadingName : null)
        ?? lines.find((line) => looksLikeName(line) && line !== company && line !== capsLine && line !== domainLine)?.replace(HONORIFIC, "")
        ?? null;
    if (!namedCompany) {
        // the line under the name (and title) in a signature: "Omar Al Suwaidi / App Review Demo Provider"
        const at = person ? lines.indexOf(person) : -1;
        const underName = at >= 0
            ? lines.slice(at + 1, at + 3).find((line) => line !== titleLine && !isTitle(line) && !/@|\d|www\.|https?:/i.test(line)
                && /^[\p{Lu}][\p{L}&.'\s-]{2,60}$/u.test(line) && line.split(" ").length <= 6) ?? null
            : null;
        // a heading that ends with who sent it: "Fleet available for Crownox missions — App Review Demo Provider"
        const dash = /^(.{2,60}?)\s[—–-]\s+([\p{Lu}][\p{L}&.'\s]{2,60})$/u.exec(lines[0] ?? "");
        // "Al Wadi Secure Logistics — Muscat": a place after the dash, so the company is before it
        const dashHead = dash && isKnownPlaceName(dash[2]) && /^[\p{Lu}]/u.test(dash[1]) && dash[1].split(" ").length <= 6 ? dash[1].trim() : null;
        const dashTail = dash && !dashHead ? dash[2].trim() : null;
        company = underName ?? dashHead ?? (dashTail && dashTail.split(" ").length <= 6 ? dashTail : null) ?? activityLine ?? domainCompany;
    }

    // an email OCR broke ("fahad@najd-armoured-demo-sa", "ops@apprev ew-demoprovider. ae"): rebuilt on the web domain
    if (!emails.length && webDomain) {
        const target = webDomain.toLowerCase().replace(/[^a-z0-9]/g, "");
        const broken = /([\w.+-]{1,40})@([\w .-]{3,60})/.exec(text);
        if (broken && withinTwoEdits(broken[2].toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, target.length + 2), target)) emails.push(`${broken[1].toLowerCase()}@${webDomain.toLowerCase()}`);
    }
    // a licence's labelled line ("Company name Sahara Armour Services L.L.C"): the name without its label
    if (company) company = company.replace(/^(?:company\s+name|name\s+of\s+(?:the\s+)?company|business\s+name|trade\s+name|company)\s*[:-]?\s+(?=\S)/i, "").trim() || company;
    return { person, company, title: title && title !== company ? title : null, emails, phones, website };
};

/**
 * Read the text off a business-card photo, in the browser (OCR loads only
 * when used; the photo never leaves the device).
 * @param file - The photo.
 */
export const readCardText = async (file: File): Promise<string> => {
    const { createWorker } = await import("tesseract.js");
    const worker = await createWorker(["eng", "ara"]);
    try {
        const { data } = await worker.recognize(file);
        return data.text ?? "";
    } finally {
        await worker.terminate();
    }
};
